import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  MessageSquare,
  CheckCircle,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    checkinDate: '',
    checkoutDate: '',
    guests: '',
    apartment: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize EmailJS when component mounts
  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    if (publicKey && publicKey !== 'your_public_key_here') {
      emailjs.init(publicKey);
    }
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation des champs obligatoires
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Veuillez remplir tous les champs obligatoires (nom, email, message).");
      return;
    }

    // Validation format email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Veuillez saisir une adresse email valide.");
      return;
    }

    setIsSubmitting(true);

    // Vérifier la configuration EmailJS
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey || 
        serviceId === 'your_service_id_here' || 
        templateId === 'your_template_id_here' || 
        publicKey === 'your_public_key_here') {
      
      // Fallback vers mailto si EmailJS n'est pas configuré
      toast.info("Configuration en cours... Utilisation du système de secours.");
      
      const subject = `Demande de renseignements - Résidence Les Platanes - ${formData.name}`;
      const emailBody = `Nouvelle demande de renseignements :

Nom : ${formData.name}
Email : ${formData.email}
Téléphone : ${formData.phone || 'Non renseigné'}
Appartement : ${formData.apartment || 'Aucune préférence'}
Personnes : ${formData.guests || 'Non renseigné'}
Arrivée : ${formData.checkinDate || 'Non renseignée'}
Départ : ${formData.checkoutDate || 'Non renseignée'}

Message : ${formData.message}`;

      const mailtoLink = `mailto:rigaud.lesplatanes@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;
      window.location.href = mailtoLink;
      
      setIsSubmitting(false);
      return;
    }

    try {
      // Préparer les paramètres du template EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'Non renseigné',
        apartment: formData.apartment || 'Aucune préférence',
        guests: formData.guests || 'Non renseigné',
        checkin_date: formData.checkinDate || 'Non renseignée',
        checkout_date: formData.checkoutDate || 'Non renseignée',
        message: formData.message,
        to_email: 'rigaud.lesplatanes@gmail.com'
      };

      // Envoyer l'email via EmailJS
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      toast.success("Votre demande a été envoyée avec succès ! Nous vous répondrons dans les plus brefs délais.");
      
      // Réinitialiser le formulaire
      setFormData({
        name: '',
        email: '',
        phone: '',
        checkinDate: '',
        checkoutDate: '',
        guests: '',
        apartment: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Erreur EmailJS:', error);
      toast.error("Erreur lors de l'envoi du message. Veuillez réessayer ou nous contacter directement à rigaud.lesplatanes@gmail.com");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="px-4 py-2 text-sm font-medium bg-emerald-100 text-emerald-800 border-emerald-200 mb-4">
              <MessageSquare className="h-4 w-4 mr-2" />
              Nous contacter
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Demande de 
              <span className="bg-gradient-to-r from-emerald-600 to-emerald-600 bg-clip-text text-transparent"> Renseignements</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Vous souhaitez réserver ou obtenir plus d'informations ? Contactez-nous, nous vous répondrons rapidement.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Informations de contact */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-2 border-emerald-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:border-emerald-300">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Phone className="h-5 w-5 text-emerald-700 mr-2" />
                    Téléphone
                  </h3>
                  <p className="text-gray-700 text-lg font-medium">+33 6 67 59 25 19</p>
                  <p className="text-gray-600 text-sm mt-1">Disponible 7j/7 de 8h à 20h</p>
                </CardContent>
              </Card>

              <Card className="border-emerald-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Mail className="h-5 w-5 text-emerald-600 mr-2" />
                    Email
                  </h3>
                  <p className="text-gray-700 text-lg font-medium">Rigaud.lesplatanes@gmail.com</p>
                  <p className="text-gray-600 text-sm mt-1">Réponse sous 24h garantie</p>
                </CardContent>
              </Card>

              <Card className="border-emerald-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <MapPin className="h-5 w-5 text-emerald-600 mr-2" />
                    Adresse
                  </h3>
                  <p className="text-gray-700">9 avenue du maréchal Joffre</p>
                  <p className="text-gray-700">34240 Lamalou-les-Bains</p>
                  <p className="text-gray-700">France</p>
                </CardContent>
              </Card>

              <Card className="border-emerald-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Clock className="h-5 w-5 text-emerald-600 mr-2" />
                    Horaires d'accueil
                  </h3>
                  <div className="space-y-2 text-gray-700">
                    <div className="flex justify-between">
                      <span>Lun - Ven:</span>
                      <span>8h00 - 20h00</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sam - Dim:</span>
                      <span>9h00 - 19h00</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Formulaire de contact */}
            <div className="lg:col-span-2">
              <Card className="border-emerald-200 shadow-lg">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                          Nom complet *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          className="mt-1 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="Votre nom complet"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                          Email *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="mt-1 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="votre.email@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                          Téléphone
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="mt-1 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="+33 6 67 59 25 19"
                        />
                      </div>
                      <div>
                        <Label htmlFor="guests" className="text-sm font-medium text-gray-700">
                          Nombre de personnes
                        </Label>
                        <Input
                          id="guests"
                          name="guests"
                          type="number"
                          value={formData.guests}
                          onChange={handleInputChange}
                          className="mt-1 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="2"
                          min="1"
                          max="10"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <Label htmlFor="checkinDate" className="text-sm font-medium text-gray-700">
                          Date d'arrivée
                        </Label>
                        <Input
                          id="checkinDate"
                          name="checkinDate"
                          type="date"
                          value={formData.checkinDate}
                          onChange={handleInputChange}
                          className="mt-1 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="checkoutDate" className="text-sm font-medium text-gray-700">
                          Date de départ
                        </Label>
                        <Input
                          id="checkoutDate"
                          name="checkoutDate"
                          type="date"
                          value={formData.checkoutDate}
                          onChange={handleInputChange}
                          className="mt-1 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="apartment" className="text-sm font-medium text-gray-700">
                          Appartement préféré
                        </Label>
                        <select
                          id="apartment"
                          name="apartment"
                          value={formData.apartment}
                          onChange={handleInputChange}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        >
                          <option value="">Sélectionner un appartement</option>
                          <option value="rembrandt">Appartement Rembrandt (F3 - Rez de jardin)</option>
                          <option value="cezanne">Appartement Cézanne (F2 - Rez de jardin)</option>
                          <option value="vangogh">Appartement Van Gogh (F2 - Rez de jardin)</option>
                          <option value="picasso">Appartement Picasso (F2 - Rez de jardin)</option>
                          <option value="monet">Appartement Monet (F2 - Rez de chaussée)</option>
                          <option value="renoir">Appartement Renoir (F3 - Rez de chaussée)</option>
                          <option value="dali">Appartement Dalí (T3 Duplex - 1er étage)</option>
                          <option value="gauguin">Appartement Gauguin (F2 - 1er étage)</option>
                          <option value="devinci">Appartement De Vinci (F3 - 1er étage)</option>
                          <option value="michelange">Appartement Michel-Ange (F3 - 2ème étage)</option>
                          <option value="degas">Appartement Degas (F3 - 2ème étage)</option>
                          <option value="aucune_preference">Aucune préférence</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-sm font-medium text-gray-700">
                        Message
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="mt-1 focus:ring-emerald-500 focus:border-emerald-500"
                        rows={4}
                        placeholder="Précisez vos besoins particuliers, questions ou demandes spéciales..."
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-800 hover:to-emerald-700 text-white font-semibold py-4 shadow-2xl hover:shadow-emerald-500/30 transition-all duration-500 border-2 border-emerald-600 hover:border-emerald-500 disabled:opacity-50 btn-hover-lift"
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Envoi en cours...</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-2">
                          <Send className="h-5 w-5" />
                          <span>Envoyer ma demande</span>
                        </div>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;